import _ from "underscore";
import {Vnode} from "mithril";
import PlayPause from "./controls/PlayPause";
import Replay from "./controls/Replay";
import Volume from "./controls/Volume";
import Seeker from "./controls/Seeker";
import CurrentTime from "./controls/CurrentTime";
import Duration from "./controls/Duration";

export type MediaPlayerConfig = {
    enableSeek: boolean,
    minElapsedTime: number,
    type: string,
    autoplay: boolean,
    skin: "full"|"mini"|"simple",
};

export default abstract class MediaPlayer {

    protected readonly link: string;
    protected onPassMinElapsedTime?: (()=>void)|null;

    protected readonly config: MediaPlayerConfig;
    public mediaElement?: HTMLAudioElement|HTMLVideoElement;
    protected _self;
    public error: string|null = null;
    public loading = true;
    public waiting = false;
    protected safeToPause = true; // https://goo.gl/LdLk22

    protected readonly controls: {playPause: PlayPause, replay: Replay, volume: Volume, currentTime: CurrentTime, duration: Duration, seeker: Seeker} & {[name:string]: {view: ()=>Vnode|null}};

    private reloadInterval: null|number = null;

    constructor(
        link: string,
        config: MediaPlayerConfig,
        onPassMinElapsedTime?: (()=>void)|null,
    ) {
        this.link = link;
        this.onPassMinElapsedTime = onPassMinElapsedTime;
        this.config = _.extend({enableSeek: false, minElapsedTime: 10, type: 'audio/mpeg', skin: "full"}, config);
        this._self = this; // due to manipulate this.mediaElement inside oncreate() method

        this.controls = {
            playPause: new PlayPause(this),
            replay: new Replay(this),
            volume: new Volume(this),
            seeker: new Seeker(this, config.enableSeek),
            currentTime: new CurrentTime(this),
            duration: new Duration(this),
        }

    }

    // Events
    protected onTimeUpdate(): void {
        if ((this.mediaElement?.currentTime || 0) > this.config.minElapsedTime && this.onPassMinElapsedTime) {
            this.onPassMinElapsedTime();
        }
    }

    protected onEnded(): void {
        if (this.onPassMinElapsedTime) {
            this.onPassMinElapsedTime();
        }
        this.pause();
    }



    // Player API
    public isPlaying(): boolean {
        return !this.mediaElement?.paused;
    }

    public isWaiting(): boolean {
        return this.loading || this.waiting;
    }

    public playPause(): void {
        if (this.isPlaying()) {
            this.pause()
        } else {
            this.play();
        }
    }

    public pause(): void {
        if (this.safeToPause) {
            this.mediaElement?.pause();
        }
    }

    public play(): void {
        const playPromise = this.mediaElement?.play();
        if (playPromise && playPromise.then) {
            this.safeToPause = false;
            playPromise.then(() => this.safeToPause = true, () => this.safeToPause = true);
        }
    }

    public replay(): void {
        this.seek(0);
        this.play();
    }

    public reload(force = false): void {
        if ((window.navigator.onLine ?? true) || force) {
            this.mediaElement?.load();
        } else {
            this.error = "No internet connection";
            this.loading = true;
        }
    }

    public volume(value?: number): number|undefined {
        if (value !== undefined && this.mediaElement?.volume !== undefined && !isNaN(value)) {
            this.mediaElement.volume = Math.min(Math.max(value, 0), 1);
        }
        return this.mediaElement?.volume;
    }

    public seek(time: number): void {
        time = Math.min(Math.max(time, 0), this.mediaElement?.duration || 0);
        if (this.mediaElement?.currentTime !== undefined) {
            this.mediaElement.currentTime = time;
        }
    }

    public currentTime(): number {
        return this.mediaElement?.currentTime || 0;
    }

    public duration(): number {
        return this.mediaElement?.duration || 0;
    }

    public enableSeeker(): void {
        this.controls.seeker.enable();
    }

    abstract view(): Vnode;

    public oninit(): void {
        this.reloadInterval = window.setInterval(() => {
            if (this.error) {
                this.reload();
            }
        }, 5000);
    }

    public onremove(): void {
        if (this.reloadInterval) {
            window.clearInterval(this.reloadInterval);
        }

        // unable to play on background
        if (this._self.mediaElement) {
            this._self.mediaElement.pause();
            this._self.mediaElement.src = '';
        }
    }
}